import type { RoleConfig } from '@/config/role-config';
import type { RouteLocationRaw } from 'vue-router';

/**
 * `/portal` is the contractor-facing template download page. It authorises
 * itself with the token in the URL fragment and must never be bounced to the
 * login screen — the people who open it have no account by design.
 */
export const PUBLIC_ROUTES = ['/login', '/portal'];

export interface NavigationTarget {
  readonly path: string;
  readonly fullPath: string;
}

export interface GuardState {
  readonly isAuthenticated: boolean;
  readonly roleConfig: RoleConfig | null | undefined;
}

/**
 * Decides where a navigation may go. Kept free of router and store access so
 * every rule below is directly unit-testable — the guard rules are the only
 * thing standing between a role and a page it may not open, and one of them
 * silently watched a path that no route ever had.
 */
export function resolveNavigationTarget(
  to: NavigationTarget,
  state: GuardState,
): RouteLocationRaw | true {
  if (!state.isAuthenticated && !PUBLIC_ROUTES.includes(to.path)) {
    return { path: '/login', query: { redirect: to.fullPath } };
  }

  if (!state.isAuthenticated) {
    return true;
  }

  if (to.path === '/login') {
    return { path: '/' };
  }

  const config = state.roleConfig;

  if (to.path.startsWith('/settings/users') && !config?.settings.canAccessUserManagement) {
    return { path: '/' };
  }

  if (to.path.startsWith('/settings') && !config?.settings.canAccess) {
    return { path: '/' };
  }

  if (
    to.path.startsWith('/settings') &&
    !to.path.startsWith('/settings/general') &&
    !config?.settings.canAccessSystemSettings
  ) {
    return { path: '/settings/general' };
  }

  /**
   * Creating a project *is* the offer import wizard — there is no other way to
   * get a bill of quantities into the system, so the route is gated on both
   * flags. It lives at `/projects/create`; an earlier guard watched
   * `/projects/import`, a path no route ever had, so the check never ran and
   * roles without import rights could deep-link straight into the wizard.
   */
  if (
    to.path.startsWith('/projects/create') &&
    !(config?.projects.canCreate && config.projects.canImport)
  ) {
    return { path: '/projects' };
  }

  /**
   * Uploading a monthly progress report is reachable only from the tracking
   * tab, so tab visibility is the role-level gate. The page itself only checks
   * project status (a finished project accepts no reports).
   */
  if (
    /^\/projects\/[^/]+\/progress\/import$/.exec(to.path) &&
    !config?.visibleProjectTabs.includes('tracking')
  ) {
    return { path: to.path.replace(/\/progress\/import$/, '') };
  }

  if (/^\/projects\/[^/]+\/edit$/.exec(to.path) && !config?.projects.canEdit) {
    return { path: to.path.replace(/\/edit$/, '') };
  }

  if (to.path.startsWith('/supervisors') && !config?.sidebar.canAccessSupervisors) {
    return { path: '/' };
  }

  if (to.path.startsWith('/contractors') && !config?.sidebar.canAccessContractors) {
    return { path: '/' };
  }

  return true;
}
